import { sharedPublicRead } from "./public-read-cache.ts";
import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";
import type { Locale } from "./homepage-seo.ts";
import type { BacktestFormState } from "./types.ts";
import { PublicReferenceCache, publicReferenceKey } from "./public-reference-cache.ts";
import {
  FetchStrategyBuilderTransport, cloneStrategyRuleDSL, parseStrategyRuleDSL,
  parseCachedStrategyBuilderCatalog, type StrategyBuilderCatalog, type StrategyRuleDSL,
} from "./strategy-builder.ts";

export type StrategyLibraryItem = {
  id: string; version: number; title: Record<string, string>; summary: Record<string, string>;
  strategy_dsl: StrategyRuleDSL; defaults: Partial<BacktestFormState>;
};
export type StrategyLibrary = { revision: string; items: StrategyLibraryItem[] };
const object = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

function translations(value: unknown, maxLength: number): Record<string, string> {
  if (!object(value) || typeof value.en !== "string" || !value.en.trim()
    || Object.values(value).some((text) => typeof text !== "string" || !text.trim() || text.length > maxLength)) {
    throw new Error("invalid_recipe_text");
  }
  return { ...value } as Record<string, string>;
}

/** A closed list of settings, never arbitrary form properties from the service. */
export function recipeDefaults(value: unknown): Partial<BacktestFormState> {
  if (!object(value)) throw new Error("invalid_recipe_defaults");
  const result: Partial<BacktestFormState> = {};
  const choices = {
    timeframe: ["timeframe", ["5m", "15m", "30m", "1h", "4h", "1d"]],
    trade_direction: ["tradeDirection", ["long", "short", "both"]],
    exit_mode: ["exitMode", ["signal_only", "signal_and_risk", "tp_sl_only"]],
    execution_model: ["executionModel", ["spot", "futures"]],
    margin_mode: ["marginMode", ["isolated", "cross"]],
  } as const;
  const numbers = {
    position_size_pct: ["positionSizePct", 0, 1, 100],
    commission_rate: ["commissionRate", 0, 0.05, 100],
    slippage_bps: ["slippageBps", 0, 500, 1],
    leverage: ["leverage", 1, 125, 1],
    stop_loss_pct: ["stopLossPct", 0, 0.5, 100],
    take_profit_pct: ["takeProfitPct", 0, 1, 100],
    maintenance_margin_rate: ["maintenanceMarginPercent", 0, 1, 100],
    funding_rate_pct: ["fundingRatePercent", -1, 1, 100],
    funding_interval_hours: ["fundingIntervalHours", 1, 168, 1],
    liquidation_fee_rate: ["liquidationFeePercent", 0, 1, 100],
  } as const;
  for (const [key, v] of Object.entries(value)) {
    const choice = choices[key as keyof typeof choices];
    const number = numbers[key as keyof typeof numbers];
    if (choice && typeof v === "string" && (choice[1] as readonly string[]).includes(v)) {
      Object.assign(result, { [choice[0]]: v });
    } else if (number && typeof v === "number" && Number.isFinite(v)
      && v >= number[1] && v <= number[2]
      && (!["position_size_pct", "stop_loss_pct", "take_profit_pct"].includes(key) || v > 0)
      && (!["maintenance_margin_rate", "funding_rate_pct", "liquidation_fee_rate"].includes(key) || v < 1)
      && (key !== "funding_rate_pct" || v > -1)
      && (key !== "funding_interval_hours" || Number.isInteger(v))) {
      Object.assign(result, { [number[0]]: v * number[3] });
    } else throw new Error("unsupported_recipe_default");
  }
  if (result.executionModel === "spot" && result.leverage !== undefined && result.leverage !== 1) {
    throw new Error("invalid_spot_leverage");
  }
  if (result.executionModel === "futures" && result.maintenanceMarginPercent !== undefined
    && result.leverage !== undefined && result.maintenanceMarginPercent / 100 >= 1 / result.leverage) {
    throw new Error("invalid_initial_margin");
  }
  return result;
}

export function parseStrategyLibrary(value: unknown, catalogs: StrategyBuilderCatalog[]): StrategyLibrary {
  if (!object(value) || value.schema_version !== 1 || typeof value.revision !== "string"
    || !/^[a-f0-9]{64}$/.test(value.revision) || !Array.isArray(value.items) || value.items.length > 200) {
    throw new Error("invalid_strategy_library");
  }
  const seen = new Set<string>();
  const items: StrategyLibraryItem[] = [];
  for (const item of value.items) {
    // A recipe requiring a newer indicator or defaults contract is independently unavailable.
    try {
      if (!object(item) || typeof item.id !== "string" || !/^[a-z][a-z0-9_]{0,63}$/.test(item.id)
        || seen.has(item.id) || !Number.isSafeInteger(item.version) || Number(item.version) < 1) {
        throw new Error("invalid_recipe");
      }
      seen.add(item.id);
      const catalog = catalogs.find((c) => object(item.strategy_dsl) && c.catalog_version === item.strategy_dsl.catalog_version);
      if (!catalog) continue;
      items.push({ id: item.id, version: Number(item.version), title: translations(item.title, 256),
        summary: translations(item.summary, 2000), strategy_dsl: parseStrategyRuleDSL(item.strategy_dsl, catalog),
        defaults: recipeDefaults(item.defaults ?? {}) });
    } catch { /* Keep other compatible recipes available. */ }
  }
  return { revision: value.revision, items };
}

export function recipeText(value: Record<string, string>, locale: string): string {
  return value[locale] ?? value[locale.split("-")[0]] ?? value.en;
}

export function selectLibraryRecipe(current: BacktestFormState, recipe: StrategyLibraryItem, locale: string): BacktestFormState {
  return { ...current, ...recipe.defaults, strategyMode: "builder", strategyMethod: undefined, unsupportedMethodExecution: undefined, useAI: false,
    strategyName: recipeText(recipe.title, locale), prompt: recipeText(recipe.summary, locale),
    strategyDSL: cloneStrategyRuleDSL(recipe.strategy_dsl) };
}

export class StrategyLibraryTransport {
  private cache: PublicReferenceCache;
  private baseURL: string;
  private fetcher: typeof fetch;
  constructor(baseURL = API_BASE_URL, fetcher: typeof fetch = browserSafeFetch, cache = new PublicReferenceCache()) {
    this.baseURL = baseURL; this.fetcher = fetcher; this.cache = cache;
  }
  private key(): string { return publicReferenceKey("strategy-library", this.baseURL, [1]); }
  private parseCache(value: unknown): StrategyLibrary {
    if (!object(value) || !Array.isArray(value.catalogs)) throw new Error("invalid_library_cache");
    const catalogs: StrategyBuilderCatalog[] = [];
    for (const catalog of value.catalogs) {
      try {
        if (object(catalog) && typeof catalog.catalog_version === "string") {
          catalogs.push(parseCachedStrategyBuilderCatalog(catalog, catalog.catalog_version));
        }
      } catch { /* unsupported */ }
    }
    return parseStrategyLibrary(value.library, catalogs);
  }
  cached(): StrategyLibrary | undefined {
    return this.cache.read(this.key(), (value) => this.parseCache(value))?.value;
  }
  async refresh(locale: Locale, force = false): Promise<StrategyLibrary> {
    const transport = new FetchStrategyBuilderTransport(this.baseURL, this.fetcher, this.cache);
    const [library, capability] = await Promise.all([
      sharedPublicRead("/api/v2/strategies/library", locale, async (signal) => {
        const response = await this.fetcher(`${this.baseURL}/api/v2/strategies/library`, { signal });
        if (!response.ok) throw new Error("strategy_library_unavailable");
        const encoded = await response.text();
        if (encoded.length > 512000) throw new Error("strategy_library_too_large");
        const value: unknown = JSON.parse(encoded);
        if (!object(value) || !Array.isArray(value.items) || value.items.length > 200) throw new Error("invalid_strategy_library");
        return value as Record<string, unknown> & { items: unknown[] };
      }, { baseURL: this.baseURL, fetcher: this.fetcher, force }),
      transport.capability(locale, force),
    ]);
    if (!capability) throw new Error("strategy_library_unavailable");
    const versions = [...new Set(library.items.flatMap((item) => object(item) && object(item.strategy_dsl)
      && typeof item.strategy_dsl.catalog_version === "string" ? [item.strategy_dsl.catalog_version] : []))];
    const catalogs = (await Promise.all(versions.filter((v) => capability.catalog_versions.includes(v))
      .map((v) => transport.catalog(capability, locale, v, force))))
      .filter((c): c is StrategyBuilderCatalog => Boolean(c));
    const parsed = parseStrategyLibrary(library, catalogs);
    this.cache.write(this.key(), { library, catalogs }, 30 * 24 * 60 * 60 * 1000);
    return parsed;
  }
}
